import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import ReactPixel from "react-facebook-pixel";
import { Page, State } from "../types";
import SelectedVenueDetailsHelper from "./SelectedVenueDetailsHelper";

interface AnalyticsPageInfo {
  path: string;
  title: string;
}

const analyticsPage: Record<Page, AnalyticsPageInfo> = {
  SelectDate: {
    path: "/resdiary-cross-sell-widget/select-date",
    title: "ResDiary Cross Sell Widget - Select Date",
  },
  SelectTime: {
    path: "/resdiary-cross-sell-widget/select-time",
    title: "ResDiary Cross Sell Widget - Select Time",
  },
  PromotionPage: {
    path: "/resdiary-cross-sell-widget/promotion",
    title: "ResDiary Cross Sell Widget - Promotion",
  },
  ContactDetails: {
    path: "/resdiary-cross-sell-widget/contact-details",
    title: "ResDiary Cross Sell Widget - Contact Details",
  },
  TermsAndConditions: {
    path: "/resdiary-cross-sell-widget/terms-and-conditions",
    title: "ResDiary Cross Sell Widget - Terms and Conditions",
  },
  PrivacyPolicy: {
    path: "/resdiary-cross-sell-widget/privacy-policy",
    title: "ResDiary Cross Sell Widget - Privacy Policy",
  },
  PaymentPage: {
    path: "/resdiary-cross-sell-widget/payment",
    title: "ResDiary Cross Sell Widget - Payment",
  },
  ConfirmDetails: {
    path: "/resdiary-cross-sell-widget/confirm-details",
    title: "ResDiary Cross Sell Widget - Confirm Details",
  },
  BookingConfirmation: {
    path: "/resdiary-cross-sell-widget/booking-confirmation",
    title: "ResDiary Cross Sell Widget - Booking Confirmation",
  },
  ErrorPage: {
    path: "/resdiary-cross-sell-widget/error",
    title: "ResDiary Cross Sell Widget - Error",
  },
};

type AnalyticsPageview = {
  path: string;
  title: string;
  micrositeName: string;
  themeId: number;
  selectedMicrositeName: string;
};

export default class AnalyticsHelper {
  static initialiseAnalytics = (state: State) => {
    const initialPageview = this.getPropertiesFromState(state);

    const ga4Id = state.crossSellSetup?.TrackingSettings?.GoogleAnalytics4Id;
    this.tryInitialiseGA4(ga4Id || "", initialPageview);

    const gtmId = state.crossSellSetup?.TrackingSettings?.GoogleTagManagerId;
    this.tryInitialiseGTM(gtmId || "", initialPageview);

    const pixelId = state.crossSellSetup?.TrackingSettings?.FacebookPixelId;
    this.tryInitialiseFacebookPixel(pixelId || "");
  };

  static tryInitialiseGA4 = (
    ga4Id: string,
    initialPageview: AnalyticsPageview
  ) => {
    if (!ga4Id) return;

    if (!ReactGA.isInitialized) {
      ReactGA.initialize(ga4Id);
    }

    this.trySendGA4Pageview(ga4Id, initialPageview);
  };

  static tryInitialiseGTM = (
    gtmId: string,
    initialPageview: AnalyticsPageview
  ) => {
    if (!gtmId) return;

    TagManager.initialize({ gtmId: gtmId });
    this.trySendGTMPageview(gtmId, initialPageview);
  };

  static tryInitialiseFacebookPixel = (pixelId: string) => {
    if (!pixelId) return;

    ReactPixel.init(pixelId);
  };

  static sendPageview = (state: State) => {
    const pageview = this.getPropertiesFromState(state);

    const ga4Id = state.crossSellSetup?.TrackingSettings?.GoogleAnalytics4Id;
    this.trySendGA4Pageview(ga4Id || "", pageview);

    const gtmId = state.crossSellSetup?.TrackingSettings?.GoogleTagManagerId;
    this.trySendGTMPageview(gtmId || "", pageview);

    const pixelId = state.crossSellSetup?.TrackingSettings?.FacebookPixelId;
    this.trySendFacebookPixelPageview(pixelId || "", pageview);
  };

  static trySendGA4Pageview = (ga4Id: string, pageview: AnalyticsPageview) => {
    if (!ga4Id || !ReactGA.isInitialized) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer: any = {
      hitType: "pageview",
      page: pageview.path,
      title: pageview.title,
      rd_microsite_name: pageview.micrositeName,
      rd_theme_id: pageview.themeId,
    };

    if (pageview.title === analyticsPage.BookingConfirmation.title) {
      dataLayer.rd_booked_microsite_name = pageview.selectedMicrositeName;
    }

    ReactGA.send(dataLayer);
  };

  static trySendGTMPageview = (gtmId: string, pageview: AnalyticsPageview) => {
    if (!gtmId) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer: any = {
      event: "ResDiaryWidgetPageview",
      rdPagePath: pageview.path,
      rdPageTitle: pageview.title,
      rdMicrositeName: pageview.micrositeName,
      rdThemeId: pageview.themeId,
    };

    if (pageview.title === analyticsPage.BookingConfirmation.title) {
      dataLayer.rdBookedMicrositeName = pageview.selectedMicrositeName;
    }

    TagManager.dataLayer({ dataLayer });
  };

  static trySendFacebookPixelPageview = (
    pixelId: string,
    pageview: AnalyticsPageview
  ) => {
    if (!pixelId) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer: any = {
      content_name: pageview.title,
      rd_page_path: pageview.path,
      rd_microsite_name: pageview.micrositeName,
      rd_theme_id: pageview.themeId,
    };

    if (pageview.title === analyticsPage.BookingConfirmation.title) {
      dataLayer.rd_booked_microsite_name = pageview.selectedMicrositeName;
    }

    ReactPixel.trackSingleCustom(pixelId, "ResDiaryWidgetPageView", dataLayer);
  };

  static getPropertiesFromState(state: State): AnalyticsPageview {
    const pageInfo = analyticsPage[state.currentPage];
    const micrositeName = state.micrositeName || "";
    const themeId = state.themeId || 0;
    const selectedMicrositeName =
      SelectedVenueDetailsHelper.getVenueDetails(state).micrositeName || "";

    return {
      path: pageInfo.path,
      title: pageInfo.title,
      micrositeName,
      themeId,
      selectedMicrositeName,
    };
  }
}
